import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = getArticleById(slug || '');

  useEffect(() => {
    if (!article) {
      navigate('/', { replace: true });
    }
  }, [article, navigate]);

  if (!article) return null;

  const relatedArticles = getRelatedArticles(article.id, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.image,
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '亚洲天堂',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://asiaparadise.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <article>
          <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} | 亚洲天堂`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60"></div>
            <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-12">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block text-xs bg-primary-500 text-white px-3 py-1 rounded-full mb-3">
                  {article.categoryName}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6 relative z-10">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]} />

            <div className="bg-background-50 rounded-lg border border-background-200/60 p-5 md:p-6 mb-6">
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-primary-500"></i>
                  <strong className="text-foreground-800">{article.author}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-primary-500"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-primary-500"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-primary-500"></i>
                  {article.destinationName}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {article.tags.map(tag => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:my-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100 mt-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block cursor-pointer"
                  >
                    <article className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-secondary-200 transition-all duration-300 h-full flex flex-col">
                      <div className="relative w-full h-[180px] md:h-[200px] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-secondary-600 transition-colors line-clamp-2 leading-snug">
                          {ra.title}
                        </h3>
                        <p className="text-foreground-600 text-xs mt-1.5 line-clamp-2 flex-1">{ra.excerpt}</p>
                        <div className="flex items-center gap-3 text-xs text-foreground-400 mt-3 pt-3 border-t border-background-200/50">
                          <span>{ra.author}</span>
                          <span>{ra.readTime}</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}